import moment from 'moment';
import { ReactNode, createContext, useEffect, useMemo } from 'react';

import { VolSurfaceToken } from '../../model/currency_type.enum';
import {
  EXCHANGE_SOURCES,
  GENERATED_SOURCES,
  classifyRegistryTokens,
} from '../../components/v2/token-selector/registry-rows';
import { useAvailableTokens } from '../exotics-tokens/use-available-tokens';
import usePersistentState from '../use-persistent-state';

// IM-2597 Market Change dashboard - shared selection provider.
//
// ONE selection (market, T1, T2) drives every panel on the dashboard. This
// provider owns only that selection; it does NOT fetch panel data. Later
// increments read the selection here and derive the per-panel change (the
// constant-maturity "shock object"). Fixed windows (24h / 7d / 30d) are a
// lookback applied to t2; 'midnight' anchors t1 at 00:00 UTC; 'custom' is set when
// the user edits a datetime directly.
export type MarketChangeWindowPreset = 'midnight' | '24h' | '7d' | '30d' | 'custom';

// Fixed-duration presets: a lookback in hours applied to t2. 'midnight' (anchored
// to midnight UTC) and 'custom' (user-edited) carry no fixed span, so neither is
// in this map.
type FixedWindowPreset = '24h' | '7d' | '30d';
export const MARKET_CHANGE_PRESET_HOURS: Record<FixedWindowPreset, number> = {
  '24h': 24,
  '7d': 24 * 7,
  '30d': 24 * 30,
};

// How the selected surface is sourced, classified from the registry `source`:
// 'exchange' (deribit) vs 'generated' (binance/mexc). `undefined` while the
// registry is still resolving or the selection matches no known source. Later
// panels gate exchange-only content on this; the provider only exposes it.
export type MarketChangeSurfaceType = 'exchange' | 'generated';

interface IMarketChangeProvider {
  market: VolSurfaceToken;
  setMarket: (market: VolSurfaceToken) => void;
  surfaceType: MarketChangeSurfaceType | undefined;
  t1: string;
  setT1: (t1: string) => void;
  t2: string;
  setT2: (t2: string) => void;
  t2IsLive: boolean;
  goLive: () => void;
  preset: MarketChangeWindowPreset;
  applyPreset: (preset: MarketChangeWindowPreset) => void;
}

interface IMarketChangeParamsProvider {
  children?: ReactNode;
}

const STATE_PATH = ['MARKET_CHANGE'];

// The market the screen opens on when nothing valid is persisted: Exchange BTC
// (the Deribit BTC registry token, baseQuote 'BTC'). Pure + exported so the
// preference is unit-tested directly.
const DEFAULT_MARKET_BASE_QUOTE = 'BTC';
export const pickDefaultMarket = (
  exchange: { baseQuote: VolSurfaceToken }[],
  generated: { baseQuote: VolSurfaceToken }[],
): VolSurfaceToken | undefined =>
  (exchange.find((t) => t.baseQuote === DEFAULT_MARKET_BASE_QUOTE) ?? exchange[0] ?? generated[0])
    ?.baseQuote;

// Defaults: a 24h window ending now (UTC). Computed once at module load so the
// persisted-state initial values are stable across the provider's first render.
const defaultT2 = moment().utc().toISOString();
const defaultT1 = moment().utc().subtract(MARKET_CHANGE_PRESET_HOURS['24h'], 'hours').toISOString();

export const MarketChangeContext = createContext({} as IMarketChangeProvider);

export const MarketChangeProvider = (props: IMarketChangeParamsProvider) => {
  const { children } = props;

  // The wire token is a bare baseQuote string (VolSurfaceToken), not a
  // CurrencyTypeEnum. Seed empty so the registry-load effect below applies the
  // default; this also treats any stale CurrencyTypeEnum value persisted by an
  // earlier scaffold build as unset (it won't match an available token).
  const [market, setMarket] = usePersistentState<VolSurfaceToken>(STATE_PATH, 'market', '');

  // Registry-driven classification, mirroring Alt Surface Builder. The default
  // prefers Exchange BTC, then any exchange token, then a generated one.
  const { tokens } = useAvailableTokens();
  const { exchange, generated } = useMemo(() => classifyRegistryTokens(tokens), [tokens]);

  // Apply a default once the registry resolves, but only when the persisted
  // market is not among the available tokens - an unset ('') or a stale
  // CurrencyTypeEnum value both fail the membership test and get replaced.
  useEffect(() => {
    const available = [...exchange, ...generated];
    if (available.length === 0) return;
    if (available.some((t) => t.baseQuote === market)) return;
    const next = pickDefaultMarket(exchange, generated);
    if (next) setMarket(next);
  }, [exchange, generated, market, setMarket]);

  // Derived source of the selected token. Classified off the registry `source`
  // (not the baseQuote), so it tracks the row the selected token lives in.
  const surfaceType = useMemo<MarketChangeSurfaceType | undefined>(() => {
    const selected = tokens.find((t) => t.baseQuote === market);
    if (!selected) return undefined;
    if (EXCHANGE_SOURCES.has(selected.source)) return 'exchange';
    if (GENERATED_SOURCES.has(selected.source)) return 'generated';
    return undefined;
  }, [tokens, market]);

  const [t1, setT1] = usePersistentState<string>(STATE_PATH, 't1', defaultT1);
  const [t2, setT2] = usePersistentState<string>(STATE_PATH, 't2', defaultT2);
  // t2 is "live" (pinned to now) until the user sets it to a past time. Presets
  // anchor t2 at now, so applying one makes it live again.
  const [t2IsLive, setT2IsLive] = usePersistentState<boolean>(STATE_PATH, 't2IsLive', true);
  const [preset, setPreset] = usePersistentState<MarketChangeWindowPreset>(
    STATE_PATH,
    'preset',
    '24h',
  );

  // A preset anchors t2 at "now" (UTC, live). Fixed presets set t1 to now minus
  // their span; 'midnight' sets t1 to 00:00 UTC; 'custom' leaves t1/t2 untouched -
  // the datetime pickers drive it.
  const applyPreset = (next: MarketChangeWindowPreset) => {
    setPreset(next);
    if (next === 'custom') return;

    const now = moment().utc();
    setT2(now.toISOString());
    setT2IsLive(true);
    if (next === 'midnight') {
      setT1(now.clone().startOf('day').toISOString());
      return;
    }
    setT1(now.clone().subtract(MARKET_CHANGE_PRESET_HOURS[next], 'hours').toISOString());
  };

  // Editing either timestamp directly drops the selection into 'custom' so the
  // preset tiles reflect that the window is no longer a fixed lookback; pinning
  // t2 to a past time also makes it historic (no longer live).
  const handleSetT1 = (next: string) => {
    setT1(next);
    setPreset('custom');
  };

  const handleSetT2 = (next: string) => {
    setT2(next);
    setPreset('custom');
    setT2IsLive(false);
  };

  // "Live" snaps t2 back to now (UTC) and marks it live, leaving t1 where it is.
  const goLive = () => {
    setT2(moment().utc().toISOString());
    setT2IsLive(true);
  };

  return (
    <MarketChangeContext.Provider
      value={{
        market,
        setMarket,
        surfaceType,
        t1,
        setT1: handleSetT1,
        t2,
        setT2: handleSetT2,
        t2IsLive,
        goLive,
        preset,
        applyPreset,
      }}
    >
      {children}
    </MarketChangeContext.Provider>
  );
};
